import React, { useCallback, useMemo, useRef, useState, useContext } from "react";
import { graphql } from "gatsby";
import Slider from "react-slick";
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import slugify from "@sindresorhus/slugify";
import { Link } from "./link";
import ScrollTabs from "./scroll-tabs";
import YoutubeContext from "../context/youtube";
import sortPosition from "../utility/sort-position";
import PlayDark from "../icons/play-dark.svg";

const settings = {
  dots: false,
  infinite: false,
  arrows: false,
  swipe: true,
  slidesToShow: 1,
  responsive: [
    {
      breakpoint: 992,
      settings: {
        arrows: true,
      },
    },
  ],
};

const ModulePromozioni = ({ module, isFirst }) => {
  const { showVideoModal } = useContext(YoutubeContext);
  const promozioni = useMemo(
    () =>
      module.tabNameFieldSet
        .filter((item) => item.tabName && item.title)
        .map((item) => ({
          ...item,
          title: item.tabName,
          subtitle: item.title,
          images: withArtDirection(getImage(item.imageFieldSet.image.node), [
            {
              media: "(max-width: 800px)",
              image: getImage(item.imageFieldSet.image.nodeMobile),
            },
          ]),
          altImage: item.imageFieldSet ? item.imageFieldSet.altImage : "",
          youtube: item.video ? item.video.split("/").reverse()[0] : null,
        }))
        .sort((a, b) => {
          const posA = parseInt(a.positiontab?.[0]);
          const posB = parseInt(b.positiontab?.[0]);
          return isNaN(posA) || isNaN(posB) ? (isNaN(posA) ? 1 : -1) : posA - posB;
        }),
    [module.tabNameFieldSet]
  );

  const tabs = useMemo(
    () => promozioni.map((item, i) => ({ id: i, label: item.title })),
    [promozioni]
  );

  const sliderRef = useRef();
  const setSlider = useCallback((slider) => (sliderRef.current = slider), []);
  const [slide, setSlide] = useState(0);
  const sliderSettings = useMemo(
    () => ({ ...settings, beforeChange: (prev, next) => setSlide(next) }),
    []
  );
  const openTab = useCallback(
    (e) => sliderRef.current.slickGoTo(parseInt(e.currentTarget.name)),
    []
  );

  return (
    <>
      {module.anchorDesktop && (
        <span id={slugify(module.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <section className="section promozioni">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              {module?.moduleTitle && (
                <>
                  {isFirst ? (
                    <h1 className="section__title">{module.moduleTitle}</h1>
                  ) : (
                    <h2 className="section__title">{module.moduleTitle}</h2>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <ScrollTabs tabs={tabs} activeTab={slide} clickTab={openTab} className="pt-0" />

        <div className="container-fluid">
          <div className="row">
            <div className="col-12 px-0">
              <Slider ref={setSlider} {...sliderSettings} className="slider-promozioni">
                {promozioni.map((item, i) => (
                  <div key={i} className="promozioni__slide">
                    <div className="row">
                      <div className="col-lg-7 order-lg-2">
                        <div className="promozioni__image">
                          <GatsbyImage image={item.images} alt={item.altImage} />
                          {item.youtube && (
                            <button
                              className="btn-play btn-play--center"
                              aria-label="Play"
                              name={item.youtube}
                              onClick={showVideoModal}
                            >
                              <PlayDark />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-5">
                        <div className="d-flex flex-column justify-content-center align-items-center h-100">
                          <div className="promozioni__content text-center text-lg-left">
                            <h3 className="promozioni__title heading-xs-semibold lg-heading-md-semibold">
                              {item.title}
                            </h3>
                            <p className="promozioni__subtitle text-sm-medium lg-heading-xs-medium">
                              {item.subtitle}
                            </p>
                            <div
                              className="promozioni__description text-sm-light"
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            ></div>
                            {item.labelFieldSet.label &&
                              item.labelFieldSet &&
                              item.labelFieldSet.link && (
                                <Link
                                  className="btn-link btn-link--white button tertiary promozioni__button"
                                  to={item.labelFieldSet.link}
                                >
                                  {item.labelFieldSet.label}
                                </Link>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
              <div className="promozioni__pages d-lg-none">
                <span>{slide + 1}</span>
                <span>&nbsp;/&nbsp;</span>
                <span>{promozioni.length}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const Promozioni = ({ data, isFirst }) => {
  const modules = useMemo(
    () =>
      data.moduleTitleFieldSet.length
        ? data.moduleTitleFieldSet
            .filter(
              (module) => module.tabNameFieldSet.filter((item) => item.tabName && item.title).length
            )
            .sort(sortPosition)
        : data.moduleTitleFieldSet,
    [data.moduleTitleFieldSet]
  );
  return (
    <>
      {modules.map((module, i) => (
        <ModulePromozioni module={module} key={i} isFirst={isFirst} />
      ))}
    </>
  );
};

export default Promozioni;
export const fragment = graphql`
  fragment PromoFragment on LiferayPromo {
    id
    liferayFields {
      siteId
      articleId
    }
    moduleTitleFieldSet {
      moduleTitle
      anchorDesktop
      anchorMobile
      position
      tabNameFieldSet {
        tabName
        title
        description
        positiontab
        imageFieldSet {
          image {
            node {
              gatsbyImageData(width: 1200, layout: CONSTRAINED)
            }
            nodeMobile: node {
              gatsbyImageData(width: 800, layout: CONSTRAINED)
            }
          }
          altImage
        }
        labelFieldSet {
          label
          link
        }
        video
      }
    }
  }
`;
